import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AllergyResponse } from '../../models/allergy.interface';
import { API_ROUTES } from '../../util/route.constants';
import { BaseResponse } from '../../models/response.interface';
import { processArrayResponse } from '../../util/utils';
import { VitalSigns } from '../../models/vital-signs.interface';
import { SoapNote } from '../../models/dictation.interface';
import { Immunization } from '../../models/immunization.interface';
import { MedicationRequest } from '../../models/medical-request.interfcae';
import { Labhistory } from '../../models/labhistory';
import { IHistoricalData } from '../../util/constants';
import moment from 'moment';
import { ConditionResponse } from '../../models/condition.interface';

@Injectable({
  providedIn: 'root',
})
export class HistoricalDataService {
  private currentEndDate: moment.Moment;
  private readonly durationInMonths: number = 3;


  constructor(private readonly http: HttpClient) {
    this.resetDateRange();
  }

  getAllergiesByPatientId$(patientId: string): Observable<AllergyResponse[]> {
    return this.http
      .get<BaseResponse<AllergyResponse[]>>(
        `${environment.cva_url}${API_ROUTES.ALLERGIES_BY_PATIENT_ID}/${patientId}`
      )
      .pipe(processArrayResponse());
  }

  getPreviousConsultationByPatientId$(patientId: string, startDate: string, endDate: string): Observable<SoapNote[]> {
    return this.http
      .get<BaseResponse<SoapNote[]>>(
        `${environment.cva_url}${API_ROUTES.PREVIOUS_CONSULTATION_BY_PATIENT_ID}/${patientId}?startDate=${startDate}&endDate=${endDate}`
      )
      .pipe(processArrayResponse());
  }

  getVitalSignsForAppointment$(
    appointmentId: string
  ): Observable<VitalSigns[]> {
    return this.http
      .get<BaseResponse<VitalSigns[]>>(
        `${environment.cva_url}${API_ROUTES.VITALS_BY_APPOINTMENT_ID}/${appointmentId}`
      )
      .pipe(processArrayResponse());
  }

  getVitalSignsForPatient$(patientId: string, startDate: string, endDate: string): Observable<VitalSigns[]> {
    return this.http
      .get<BaseResponse<VitalSigns[]>>(
        `${environment.cva_url}${API_ROUTES.VITALS_BY_PATIENT_ID}/${patientId}?startDate=${startDate}&endDate=${endDate}`
      )
      .pipe(processArrayResponse());
  }

  getImmunizationByPatientId$(patientId: string): Observable<Immunization[]> {
    return this.http
      .get<BaseResponse<Immunization[]>>(
        `${environment.cva_url}${API_ROUTES.IMMUNIZATION_BY_PATIENT_ID}/${patientId}`
      )
      .pipe(processArrayResponse());
  }

  getMedicationsForPatient$(patientId: string, startDate: string, endDate: string): Observable<MedicationRequest[]> {
    return this.http.get<BaseResponse<MedicationRequest[]>>(
      `${environment.cva_url}${API_ROUTES.MEDICATION_REQUEST_BY_PATIENT_ID}/${patientId}?startDate=${startDate}&endDate=${endDate}`
    ).pipe(processArrayResponse());
  }

  getLabResults$(patientId: string): Observable<Labhistory[]> {
    return this.http
      .get<BaseResponse<Labhistory[]>>(
        `${environment.cva_url}${API_ROUTES.LAB_RESULTS}/${patientId}`
      )
      .pipe(processArrayResponse());
  }

  getConditionsByPatientId$(patientId: string, startDate: string, endDate: string): Observable<ConditionResponse[]> {

    return this.http
      .get<BaseResponse<ConditionResponse[]>>(
        `${environment.cva_url}${API_ROUTES.CONDITION}/patient/${patientId}?startDate=${startDate}&endDate=${endDate}`
      )
      .pipe(
        processArrayResponse()
      );
  }

  getHistoricalDataForAll$(patientId: string, startDate: string, endDate: string): Observable<IHistoricalData> {
    return this.http.get<BaseResponse<IHistoricalData>>(`${environment.cva_url}${API_ROUTES.HISTORICAL_DATA}/${patientId}?startDate=${startDate}&endDate=${endDate}`)
      .pipe(processArrayResponse());
  }

  generateCdmPlan$(payload: Record<string, unknown>): Observable<unknown> {
    return this.http
      .post<{ response?: unknown }>(`${environment.cva_url}${API_ROUTES.AI_PROXY}${API_ROUTES.GENERATE_CDM_PLAN}`, payload)
      .pipe(map((res) => res?.response ?? res));
  }

  generateHistorySummary$(payload: { history: unknown[] }): Observable<unknown> {
    return this.http
      .post<{ response?: unknown }>(`${environment.cva_url}${API_ROUTES.AI_PROXY}${API_ROUTES.GENERATE_HISTORY_SUMMARY}`, payload)
      .pipe(map((res) => res?.response ?? res));
  }

  resetDateRange(): void {
    this.currentEndDate = moment().endOf('day');
  }

  getNextRange(): { startDate: string; endDate: string } {
    const endDate = this.currentEndDate.clone();
    const startDate = endDate.clone()
      .subtract(this.durationInMonths, 'months')
      .startOf('day');

    // Update currentEndDate for next call (move backward)
    this.currentEndDate = startDate.clone().subtract(1, 'day').endOf('day');

    return {
      startDate: startDate.utc().toISOString(),
      endDate: endDate.utc().toISOString()
    };
  }
}
